import { Create, useSelect } from "@refinedev/antd";
import { useCreate } from "@refinedev/core";
import {
  App,
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Steps,
} from "antd";
import type { Season } from "../../types";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router";
import { usePageTitle } from "../../hooks";

interface PlanningGroupCreateFormValues {
  name: string;
  season: number;
  audit_company: number;
}

/**
 * PlanningGroupCreate — Step 1 of a 2-step planning creation flow.
 *
 * Step 1 (this page): captures the planning's basic data (name,
 * season, audit company, estimated dates).
 *
 * Step 2 (route `/planning-groups/show/:id/assign-audits`): once the
 * planning is persisted we redirect there so the admin picks the
 * farms / audit types to seed the planning with. The same screen is
 * also reachable from the planning detail later on, so editing the
 * roster after the fact uses the same UI.
 */
export const PlanningGroupCreate = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  // We don't need to do anything with the country once the season is
  // chosen — the assign-audits page recomputes it server-side. We
  // just remember the season label so we can show a hint at the
  // bottom of the form.
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);

  usePageTitle(t("planning.create", "Crear Planificación"));

  // The wizard visual is driven by static <Steps>: step 1 lives
  // here, step 2 is the assign-audits page navigated to after save.
  const [form] = Form.useForm<PlanningGroupCreateFormValues>();
  const { mutateAsync: createPlanning } = useCreate();

  const { selectProps: seasonSelectProps, query: seasonQuery } = useSelect<Season>({
    resource: "seasons",
    optionLabel: "name",
    optionValue: "id",
  });

  // Endpoint lives at /api/v1/admin/audit-companies/ — resource path
  // must include the `v1/` prefix, otherwise the data provider hits
  // /api/admin/audit-companies/ and Django returns 404.
  const { selectProps: auditCompanySelectProps } = useSelect<{ id: number; name: string }>({
    resource: "v1/admin/audit-companies",
    optionLabel: "name",
    optionValue: "id",
    filters: [{ field: "is_active", operator: "eq", value: true }],
    pagination: { pageSize: 200 },
  });

  const handleSeasonChange = (seasonId: number) => {
    const seasons = seasonQuery?.data?.data || [];
    const season = seasons.find((s: Season) => s.id === seasonId);
    if (season) setSelectedSeason(season);
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    const values = form.getFieldsValue(true) as PlanningGroupCreateFormValues;
    setIsSaving(true);
    try {
      // start_date / finish_date are still NOT NULL on the model but
      // the user no longer sees them — they were superseded by the
      // derived first_audit_date / last_audit_date (computed from the
      // assigned audits). We send today + today+90 here so the
      // record validates; the values are not surfaced anywhere in
      // the UI.
      const today = dayjs();
      const payload = {
        name: values.name,
        start_date: today.format("YYYY-MM-DD"),
        finish_date: today.add(90, "day").format("YYYY-MM-DD"),
        season: values.season,
        audit_company: values.audit_company,
      };
      const res = await createPlanning({
        resource: "planning-groups",
        values: payload,
        successNotification: false,
      });
      const planningId = (res?.data as any)?.id;
      if (!planningId) {
        throw new Error("No se obtuvo el ID de la planificación");
      }
      message.success(
        t("planning.create_success", "Planificación creada — asigna ahora las auditorías."),
      );
      // Drop the user straight into step 2 (the assign-audits page).
      // `?from=create` makes that page render the 2-step wizard
      // header so the flow feels contiguous.
      navigate(`/planning-groups/show/${planningId}/assign-audits?from=create`);
    } catch (error: any) {
      console.error("Error creating planning group:", error);
      message.error(
        error?.message ||
          t("planning.create_error", "Error al crear la planificación"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Create
      title={t("planning.create", "Crear Planificación")}
      footerButtons={() => (
        <Space>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isSaving}
          >
            {t("planning.create_continue", "Continuar")}
          </Button>
        </Space>
      )}
    >
      <Steps current={0} style={{ marginBottom: 24 }}>
        <Steps.Step
          title={t("planning.basic_info", "Datos básicos")}
        />
        <Steps.Step
          title={t("planning.assign_audits", "Asignar auditorías")}
        />
      </Steps>

      <Form form={form} layout="vertical">
        <Form.Item
          label={t("planning.name")}
          name="name"
          rules={[
            {
              required: true,
              message: `${t("planning.name")} ${t("common.required")}`,
            },
            { max: 200, message: "El nombre no puede exceder 200 caracteres" },
          ]}
        >
          <Input placeholder={t("planning.name") as string} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={t("planning.season")}
              name="season"
              rules={[
                {
                  required: true,
                  message: `${t("planning.season")} ${t("common.required")}`,
                },
              ]}
            >
              <Select
                options={seasonSelectProps.options}
                placeholder={t("planning.season") as string}
                showSearch
                onChange={handleSeasonChange}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={t("planning.audit_company", "Empresa Auditora")}
              name="audit_company"
              tooltip={t(
                "planning.audit_company_tooltip",
                "La empresa que realizará la auditoría. Su coordinador asignará después los auditores concretos.",
              )}
              rules={[
                {
                  required: true,
                  message: `${t("planning.audit_company", "Empresa Auditora")} ${t("common.required")}`,
                },
              ]}
            >
              <Select
                {...auditCompanySelectProps}
                placeholder={
                  t(
                    "planning.audit_company_placeholder",
                    "Seleccione la empresa auditora",
                  ) as string
                }
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {selectedSeason && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "#f0f2f5",
              borderRadius: 4,
            }}
          >
            <strong>{t("planning.country")}:</strong>{" "}
            {selectedSeason.country?.name}
          </div>
        )}
      </Form>
    </Create>
  );
};
